import { useTranslation } from '@/hooks/useTranslation';
import { useEffect, useRef, useState } from 'react';
import { useReveal } from '@/hooks/useReveal';
import { Building2, Mail, Globe } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// ⬇ ANTI-SPAM: Cloudflare Turnstile loaded from CDN (no npm dependency).
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

const iconMap: Record<string, LucideIcon> = {
  building: Building2,
  mail: Mail,
  globe: Globe,
};

const ContactSection = () => {
  const { t, language } = useTranslation();
  const [formSent, setFormSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [service, setService] = useState('');
  const [message, setMessage] = useState('');

  // ⬇ ANTI-SPAM 1: honeypot. Real users never see or fill this — must stay empty.
  const [company, setCompany] = useState('');
  // ⬇ ANTI-SPAM 2: timing. When the form first rendered (bots submit near-instantly).
  const renderedAt = useRef<number>(Date.now());
  // ⬇ ANTI-SPAM 3: Turnstile token from the widget.
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useReveal();

  // ⬇ ANTI-SPAM 3: load the Turnstile script and render the widget.
  useEffect(() => {
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
    if (!siteKey) {
      console.warn('VITE_TURNSTILE_SITE_KEY is not set — Turnstile will not render.');
      return;
    }

    const renderWidget = () => {
      if (window.turnstile && widgetRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          callback: (tok: string) => setToken(tok),
          'expired-callback': () => setToken(null),
          'error-callback': () => setToken(null),
          theme: 'auto',
        });
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`);
      if (!script) {
        script = document.createElement('script');
        script.src = TURNSTILE_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', renderWidget);
      // Fallback in case the script was already loaded by another instance.
      const iv = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(iv);
          renderWidget();
        }
      }, 200);
      window.setTimeout(() => window.clearInterval(iv), 8000);
    }

    return () => {
      if (window.turnstile && widgetIdRef.current) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, []);

  const resetTurnstile = () => {
    setToken(null);
    if (window.turnstile && widgetIdRef.current) {
      try { window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ⬇ ANTI-SPAM: honeypot tripped — silently ignore.
    if (company) return;
    // ⬇ ANTI-SPAM: require a Turnstile token before sending.
    if (!token) {
      toast.error('Please complete the verification.');
      return;
    }

    setSending(true);
    try {
      const { error } = await supabase.functions.invoke('submit-contact', {
        body: {
          firstName,
          lastName,
          email,
          service,
          message,
          language,
          // ⬇ ANTI-SPAM fields checked server-side in the edge function.
          company, // honeypot — must be empty
          elapsedMs: Date.now() - renderedAt.current, // time-to-submit
          turnstileToken: token, // verified against Cloudflare
        },
      });
      if (error) throw error;
      setFormSent(true);
      setFirstName('');
      setLastName('');
      setEmail('');
      setService('');
      setMessage('');
      resetTurnstile();
    } catch (err: any) {
      toast.error(err.message || 'Error sending message');
      resetTurnstile();
    }
    setSending(false);
  };

  return (
    <section id="contact">
      <div className="contact-wrap">
        <div className="contact-left rev-l">
          <div className="section-tag">{t.contact.tag}</div>
          <h2 className="section-h" style={{ whiteSpace: 'pre-line' }}>{t.contact.title}</h2>
          <p className="section-sub">{t.contact.subtitle}</p>

          <div className="c-info-list">
            {t.contact.info.map((item, i) => {
              const Icon = iconMap[item.icon] || Mail;
              return (
                <div className="c-item" key={i}>
                  <div className="c-icon"><Icon size={18} /></div>
                  <div>
                    <h4>{item.title}</h4>
                    {item.href ? (
                      <a href={item.href}>{item.text}</a>
                    ) : (
                      <p>{item.text}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="contact-form-wrap rev-r">
          <h3 className="form-title">{t.contact.form.title}</h3>
          <form onSubmit={handleSubmit}>
            <div className="f-row">
              <div className="f-group">
                <label className="f-label">{t.contact.form.firstName}</label>
                <input className="f-input" type="text" required value={firstName} onChange={e => setFirstName(e.target.value)} />
              </div>
              <div className="f-group">
                <label className="f-label">{t.contact.form.lastName}</label>
                <input className="f-input" type="text" required value={lastName} onChange={e => setLastName(e.target.value)} />
              </div>
            </div>
            <div className="f-group">
              <label className="f-label">{t.contact.form.email}</label>
              <input className="f-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="f-group">
              <label className="f-label">{t.contact.form.service}</label>
              <select className="f-select" required value={service} onChange={e => setService(e.target.value)}>
                {t.contact.form.serviceOptions.map((opt, i) => (
                  <option key={i} value={i === 0 ? '' : opt} disabled={i === 0}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="f-group">
              <label className="f-label">{t.contact.form.message}</label>
              <textarea className="f-textarea" required value={message} onChange={e => setMessage(e.target.value)} />
            </div>

            {/* ⬇ ANTI-SPAM 1: honeypot field. Hidden from humans + screen readers. */}
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}
            >
              <label>
                Company (leave this field empty)
                <input
                  type="text"
                  name="company"
                  tabIndex={-1}
                  autoComplete="off"
                  value={company}
                  onChange={e => setCompany(e.target.value)}
                />
              </label>
            </div>

            {/* ⬇ ANTI-SPAM 3: Turnstile widget renders into this container. */}
            <div className="f-group" style={{ marginTop: 8 }} ref={widgetRef} />

            <button type="submit" className="f-submit" disabled={sending}>
              {sending ? '...' : t.contact.form.send}
            </button>
            {formSent && (
              <p style={{ color: '#4CAF50', fontWeight: 700, fontSize: '.85rem', marginTop: '16px', textAlign: 'center' }}>
                {t.contact.form.success}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
